import Portal from '@mui/material/Portal';

import { Iconify } from '../iconify/Iconify';
import { snackbarClasses } from './classes';
import { SnackbarRoot } from './styles';

/**
 * Snackbar component that provides toast notifications using a Portal.
 *
 * This component renders a customized toast notification system positioned at the top-right of the screen.
 * It supports various toast types (default, info, error, success, warning) with custom styling and icons.
 *
 * Features:
 * - Displays up to 4 visible toasts at once
 * - Shows close button on toasts
 * - Provides custom icons for different notification types
 * - Applies custom styling via CSS classes
 * - Positioned at top-right with 16px offset
 *
 * @returns React component that renders the snackbar system within a Portal
 */
export function Snackbar() {
    return (
        <Portal>
            <SnackbarRoot
                expand
                gap={12}
                closeButton
                offset={16}
                visibleToasts={4}
                position="top-right"
                className={snackbarClasses.root}
                toastOptions={{
                    unstyled: true,
                    classNames: {
                        toast: snackbarClasses.toast,
                        icon: snackbarClasses.icon,
                        // content
                        content: snackbarClasses.content,
                        title: snackbarClasses.title,
                        description: snackbarClasses.description,
                        // button
                        actionButton: snackbarClasses.actionButton,
                        cancelButton: snackbarClasses.cancelButton,
                        closeButton: snackbarClasses.closeButton,
                        // state
                        default: snackbarClasses.default,
                        info: snackbarClasses.info,
                        error: snackbarClasses.error,
                        success: snackbarClasses.success,
                        warning: snackbarClasses.warning,
                    },
                }}
                icons={{
                    loading: <span className={snackbarClasses.loadingIcon} />,
                    info: (
                        <Iconify
                            className={snackbarClasses.iconSvg}
                            icon="solar:info-circle-bold"
                        />
                    ),
                    success: (
                        <Iconify
                            className={snackbarClasses.iconSvg}
                            icon="solar:check-circle-bold"
                        />
                    ),
                    warning: (
                        <Iconify
                            className={snackbarClasses.iconSvg}
                            icon="solar:danger-triangle-bold"
                        />
                    ),
                    error: <Iconify className={snackbarClasses.iconSvg} icon="solar:danger-bold" />,
                }}
            />
        </Portal>
    );
}
